// Обучение T01–T10 (E16-T02, E16-T03; ux/11 §1.3–1.4; ГД 12; T07–T10 — CONTRACT Р-К102). Шаг хранится на сервере: клиент шлёт
// POST /tutorial/step при завершении шага и берёт следующий из ответа (решение каркаса Р-35).
// Ошибка сервера в шаге: тост «Что-то заело», тихие повторы 1/2/4 с, затем «Повторить» в пузыре — без предела;
// SCR-FATAL в обучении не показывается (ux/11 §1.1, У-К-44). STEP_OUT_OF_ORDER — берём шаг из ответа.
import { signal } from '@preact/signals';
import type { BattleState } from '@garages/contract';
import type { App } from '../app/app.js';
import { track } from '../analytics/analytics.js';
import { ApiCallError } from '../net/api-client.js';
import { player, saveCache, tutorialActive, tutorialStep } from '../state/game.js';
import { storage } from '../state/storage.js';
import { showToast } from '../ui/toast.js';
import { t as tr } from '../i18n/t.js';

/** Цель шага: data-tut элемента, реплика Семёныча, подсказка; retry — кнопка «Повторить» в пузыре. */
export interface TutUi { step: string; target: string | null; say: string | null; hint: string | null; retry: (() => void) | null; bubbleTop?: boolean; next?: (() => void) | null }
export const tut = signal<TutUi | null>(null);

export const TEXT = {
  T01auto: tr('TUT-T01-AUTO'),
  T02say: tr('TUT-T02-01'),
  T02hint: tr('TUT-T02-02'),
  T02more: tr('TUT-T02-03'),
  T02auto: tr('TUT-T02-AUTO'),
  T02done: tr('TUT-T02-04'),
  T03say: tr('TUT-T03-01'),
  T03hint: tr('TUT-T03-03'),
  T03next: tr('TUT-T03-05'),
  T03again: tr('TUT-T03-R'),
  T04say: tr('TUT-T04-02'),
  T04hint: tr('TUT-T04-03'),
  T04cd: tr('TUT-T04-04'),
  T04ready: tr('TUT-T04-06'),
  T05say: tr('TUT-T05-05'),
  T05level: tr('TUT-T05-04'),
  T06hint: tr('TUT-T06-01'),
  T06say: tr('TUT-T06-04'),
  T06after: tr('TUT-T06-03'),
  T07say: tr('TUT-T07-01'), T07hint: tr('TUT-T07-02'), T07zone: tr('TUT-T07-03'), T07btn: tr('TUT-T07-04'), T07done: tr('TUT-T07-06'), T07again: tr('TUT-T07-R'),
  T08say: tr('TUT-T08-01'), T08inspect: tr('TUT-T08-02'), T08nodes: tr('TUT-T08-03'), T08btn: tr('TUT-T08-04'), T08done: tr('TUT-T08-05'), T08again: tr('TUT-T08-R'),
  T09say: tr('TUT-T09-01'), T09hint: tr('TUT-T09-02'), T09join: tr('TUT-T09-06'), T09later: tr('TUT-T09-07'), T09joined: tr('TUT-T09-08'), T09skipped: tr('TUT-T09-09'), T09again: tr('TUT-T09-R'),
  T10say: tr('TUT-T10-01'), T10box: tr('TUT-T10-03'), T10open: tr('TUT-T10-04'), T10inside: tr('TUT-T10-05'), T10after: tr('TUT-T10-11'), T10again: tr('TUT-T10-R'),
  err: tr('CL-TUTORIAL-FLOW-01'),
} as const;

/** «Первая посылка» T10 — фиксированный состав (ГД 12 §3, economy/04 §7.2): вместо «Шансы» — «Что внутри». */
export const FIRST_BOX = { currencies: { nuts: 300, diesel: 10, keys: 1, tickets: 2 } } as const;

const RETRY_MS = [1000, 2000, 4000];
const started = new Map<string, number>();

export function setTut(ui: Partial<TutUi> | null): void {
  if (!ui) { tut.value = null; return; }
  tut.value = { step: tutorialStep.value, target: null, say: null, hint: null, retry: null, ...tut.value, ...ui };
}

/** T01–T06 — навигация скрыта; T07–T10 — видна, но тапы мимо цели гасит затемнение (ux/11 §1.3). */
function lockNav(app: App, step: string) {
  app.nav.setTutorial(tutorialActive(step) ? { step, navHidden: step <= 'T06', backLocked: step <= 'T04' } : null);
}

/** Шаг завершён: POST /tutorial/step с тихими повторами; возвращает ответ сервера. */
export async function completeStep(app: App, step: string, action?: string) {
  for (let attempt = 0; ; attempt++) {
    try {
      const r = await app.call('tutorialStep', { body: { step, ...(action ? { action } : {}) }, noRetry: true });
      track('tutorial_step_complete', { step, ms: Date.now() - (started.get(step) ?? Date.now()) });
      tutorialStep.value = r.step;
      return r;
    } catch (e) {
      if (e instanceof ApiCallError && e.code === 'STEP_OUT_OF_ORDER') {
        const cur = String(e.details?.['current'] ?? e.details?.['step'] ?? tutorialStep.value);
        app.reporter.report('network', new Error(`tutorial ${step} → ${cur}`));
        tutorialStep.value = cur;
        startTutorial(app, cur);
        throw e;
      }
      app.reporter.report('network', e instanceof Error ? e : new Error(String(e)));
      if (attempt < RETRY_MS.length) {
        if (attempt === 0) showToast(TEXT.err, 'error');
        await new Promise((r) => setTimeout(r, RETRY_MS[attempt]));
        continue;
      }
      // Без предела: ждём кнопку «Повторить» в пузыре Семёныча.
      await new Promise<void>((resolve) => setTut({ retry: () => { setTut({ retry: null }); resolve(); } }));
      attempt = -1;
    }
  }
}

/** Войти в шаг (старт игры, повторный вход, STEP_OUT_OF_ORDER). */
export function startTutorial(app: App, step: string, active?: BattleState | null): void {
  // Повторный вход в тот же шаг (перезапуск игры) — реплика «…-R» вместо первой (ГД 12 §5).
  const again = storage.get('tutStepSeen') === step;
  storage.set('tutStepSeen', step);
  tutorialStep.value = step;
  started.set(step, Date.now());
  track('tutorial_step_start', { step, ms_since_open: Math.round(performance.now()) });
  lockNav(app, step);
  if (!tutorialActive(step)) { finishTutorial(app); return; }
  const { nav } = app;
  switch (step) {
    case 'T01':
      setTut({ step, target: 'take-key', say: null, hint: null });
      nav.showSystem('SCR-TUT-LETTER', {});
      break;
    case 'T02':
      nav.hideSystem();
      nav.selectTab('home');
      setTut({ step, target: 'lock', say: TEXT.T02say, hint: TEXT.T02hint });
      break;
    case 'T03':
    case 'T04':
      nav.hideSystem();
      setTut({ step, target: 'move-strike', say: step === 'T03' ? TEXT.T03say : TEXT.T03again, hint: TEXT.T03hint });
      void startTutorialBattle(app, step, active);
      break;
    case 'T05':
      nav.hideSystem();
      refreshPlayer(app);
      setTut({ step, target: 'claim', say: TEXT.T05say, hint: null });
      nav.selectTab('map');
      nav.push('SCR-FIGHT-RESULT', { tutorial: true, bossId: 'B01-T' });
      break;
    case 'T06':
      nav.hideSystem();
      setTut({ step, target: 'tut-cap', say: null, hint: TEXT.T06hint });
      nav.selectTab('home');
      nav.push('SCR-INVENTORY', { tutorial: true });
      break;
    case 'T07':
      nav.hideSystem();
      nav.closeSheets();
      nav.selectTab('home');
      setTut({ step, target: 'zone-G01', say: again ? TEXT.T07again : TEXT.T07say, hint: TEXT.T07hint, next: null });
      break;
    case 'T08':
      nav.hideSystem();
      nav.closeSheets();
      nav.selectTab('home');
      setTut({ step, target: 'home-car', say: again ? TEXT.T08again : TEXT.T08say, hint: null, next: null });
      break;
    case 'T09':
      nav.hideSystem();
      nav.closeSheets();
      nav.selectTab('coop');
      setTut({ step, target: 'tut-coop-list', say: again ? TEXT.T09again : TEXT.T09say, hint: TEXT.T09hint, next: null });
      break;
    case 'T10':
      nav.hideSystem();
      nav.closeSheets();
      nav.selectTab('quests');
      setTut({ step, target: 'tut-first-box', say: again ? TEXT.T10again : TEXT.T10box, hint: TEXT.T10inside, next: null });
      break;
  }
}

/** Реплика Семёныча с кнопкой «Дальше» и без цели: следующий шаг — по кнопке (T07–T09). */
function sayThen(say: string, hint: string | null, then: () => void): void {
  setTut({ target: null, say, hint, next: () => { setTut({ next: null }); then(); } });
}

/** T07: «Улучшить» Верстак — бесплатно и мгновенно; уровень выдаёт сервер по ключу шага (E16-T01). */
export async function tutT07Upgrade(app: App): Promise<void> {
  const r = await completeStep(app, 'T07');
  app.haptic('success');
  app.nav.closeSheets();
  app.nav.openSheet('MOD-ZONE-UPGRADED', { zoneId: 'G01', level: 1 });
  sayThen(TEXT.T07done, null, () => startTutorial(app, r.step));
}

/** T08: «Починить» двигатель — бесплатно, до ур. 1; выезды — с 4-го уровня. */
export async function tutT08Repair(app: App, afterHint: string): Promise<void> {
  const r = await completeStep(app, 'T08');
  app.haptic('heavy');
  app.nav.closeSheets();
  sayThen(TEXT.T08done, afterHint, () => { app.nav.pop(); startTutorial(app, r.step); });
}

/** T09: вступил в кооператив или «Позже» — шаг закрывается в обоих случаях. */
export async function tutT09Done(app: App, joined: boolean): Promise<void> {
  const r = await completeStep(app, 'T09', joined ? 'joined' : 'later');
  if (joined) app.haptic('success');
  app.nav.closeSheets();
  sayThen(joined ? TEXT.T09joined : TEXT.T09skipped, null, () => startTutorial(app, r.step));
}

/** T10: «Открыть» первую посылку — состав фиксирован, выдаёт сервер (повтор шага второй раз не выдаёт). */
export async function tutT10Open(app: App): Promise<void> {
  const r = await completeStep(app, 'T10');
  app.haptic('success');
  app.nav.openSheet('MOD-REWARD', { grant: r.rewards ?? FIRST_BOX });
  setTut({ target: null, say: TEXT.T10after, hint: null, next: () => { app.nav.closeSheets(); startTutorial(app, r.step); } });
}

/** Уровень и характеристики после учебной победы (T05 даёт уровень 2) — шапка не должна показывать старое. */
export function refreshPlayer(app: App): void {
  app.call('getMe').then((p) => { player.value = p; saveCache(); }).catch(() => {});
}

/**
 * Учебный бой T03–T04. Восстановление после перезапуска (ГД 12 §5): активный B01-T — продолжаем его с состояния
 * сервера; на T04 без активного боя, если победа уже была, сервер примет шаг T04 (Р-33) — бой не повторяем.
 */
async function startTutorialBattle(app: App, step: string, known?: BattleState | null) {
  try {
    const active = known !== undefined ? known : await app.call('getActiveBattle');
    if (active && active.boss.bossId === 'B01-T') {
      app.nav.selectTab('map');
      app.nav.push('SCR-FIGHT', { state: active, restored: true, tutorial: true });
      return;
    }
    if (step === 'T04') {
      try {
        const r = await app.call('tutorialStep', { body: { step: 'T04' }, noRetry: true });
        tutorialStep.value = r.step;
        startTutorial(app, r.step);
        return;
      } catch (e) {
        // `reason: 'battle'` — победы ещё нет: бой начинается заново (ГД 12 §5)
        if (!(e instanceof ApiCallError && e.code === 'STEP_OUT_OF_ORDER')) throw e;
      }
    }
    const start = await app.call('startBattle', { body: { bossId: 'B01-T', mode: 'solo' }, idempotencyKey: app.idem() });
    app.nav.selectTab('map');
    app.nav.push('SCR-FIGHT', { start, tutorial: true });
  } catch (e) {
    app.reporter.report('network', e instanceof Error ? e : new Error(String(e)));
    showToast(TEXT.err, 'error');
    setTut({ retry: () => { setTut({ retry: null }); void startTutorialBattle(app, tutorialStep.value); } });
  }
}

/** Подсветка в учебном бою по готовым приёмам: ключ готов — бросок, иначе удар (ГД 12 T04). */
export function fightTarget(moves: { moveId: string; ready: boolean }[], turnNo: number, round = turnNo + 1): { target: string; say: string | null; hint: string } {
  // «Бросок ключа» — с 3-го раунда (ГД 12 T04, Р-41), даже если старый сервер прислал его раньше.
  const wrench = round >= 3 ? moves.find((m) => m.moveId === 'wrench') : undefined;
  if (wrench?.ready) return { target: 'move-wrench', say: turnNo === 2 ? TEXT.T04say : null, hint: turnNo >= 4 ? TEXT.T04ready : TEXT.T04hint };
  if (wrench && !wrench.ready) return { target: 'move-strike', say: null, hint: TEXT.T04cd };
  return { target: 'move-strike', say: turnNo === 0 ? TEXT.T03say : null, hint: turnNo === 1 ? TEXT.T03next : TEXT.T03hint };
}

/** После ходов учебного боя: 2-й раунд закрывает T03, победа — T04 (Р-35). */
export async function onTutorialTurn(app: App, turnNo: number, won: boolean): Promise<void> {
  // ≥ 2, а не === 2: бой мог восстановиться после перезапуска уже позже 2-го раунда
  if (turnNo >= 2 && tutorialStep.value === 'T03') await completeStep(app, 'T03');
  if (won && tutorialStep.value === 'T04') await completeStep(app, 'T04');
}

export function finishTutorial(app: App): void {
  app.nav.setTutorial(null);
  tut.value = null;
  if (storage.get('tutDoneShown') !== '1') {
    storage.set('tutDoneShown', '1');
    app.nav.openSheet('MOD-TUT-DONE');
  }
}
